
import React, { useState, useEffect, useCallback,useRef } from "react";
import {
  Box, Typography, Tabs, Tab, Card, CardContent, CardActions, Button,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stepper, Step, StepLabel, Alert, Snackbar, CircularProgress,
  Avatar, Divider, IconButton, InputAdornment, Paper, Stack,
  useMediaQuery, useTheme as useMuiTheme, Container,Grid,Checkbox, FormControlLabel
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import {
    LocalHospital as LocalHospitalIcon,
    FitnessCenter as FitnessCenterIcon,
    CurrencyRupee as CurrencyRupeeIcon,
    AccessTime as AccessTimeIcon,
    BookmarkAdd as BookmarkAddIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    History as HistoryIcon,
    Phone as PhoneIcon,
    Person as PersonIcon,
    LocationOn as LocationOnIcon,
    Event as EventIcon,
    Close as CloseIcon,
    Schedule as ScheduleIcon,
    MedicalServices as MedicalServicesIcon,
    Tag as TagIcon,
    ArrowBack as ArrowBackIcon,
    VerifiedUser as VerifiedUserIcon,
    Star as StarIcon,
    CalendarToday as CalendarTodayIcon
  } from "@mui/icons-material";
  import axios from 'axios';
import  bg from "../Logos/med_icon3.png"
import { ChevronLeft, Edit2, Tag, ChevronDown, ChevronUp, Plus, Minus, Trash2, X } from 'lucide-react';


// ── Header layout constants — must match Header.jsx exactly ──────────────────
const DRAWER_WIDTH = 280;
const MOBILE_BAR_H = 56;

const API = process.env.REACT_APP_API;

// ── helpers outside component — avoids stale closure in useCallback ──────────
function getAuthHeader() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}
function getUserId() {
  return sessionStorage.getItem("userId") || localStorage.getItem("userId") || null;
}

// ── Ayuja-exact theme ────────────────────────────────────────────────────────
const ayujaTheme = createTheme({
  palette: {
    mode: "light",
    primary:    { main: "#006D77", light: "#00949f", dark: "#004d55", contrastText: "#fff" },
    secondary:  { main: "#E29578" },
    success:    { main: "#16a34a" },
    warning:    { main: "#d97706" },
    error:      { main: "#dc2626" },
    info:       { main: "#0284c7" },
    background: { default: "#f0f7f8", paper: "#ffffff" },
    text:       { primary: "#0f2b2e", secondary: "#4a6b6f" },
  },
  typography: {
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    h4: { fontWeight: 800, letterSpacing: -0.5 },
    h5: { fontWeight: 800 },
    h6: { fontWeight: 700 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 12px rgba(0,109,119,0.08)",
          border: "1px solid rgba(0,109,119,0.1)",
          transition: "box-shadow 0.25s ease, transform 0.25s ease",
          "&:hover": {
            boxShadow: "0 8px 28px rgba(0,109,119,0.18)",
            transform: "translateY(-3px)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 700, borderRadius: 8 },
        containedPrimary: {
          background: "linear-gradient(135deg, #006D77 0%, #00949f 100%)",
          boxShadow: "0 4px 14px rgba(0,109,119,0.3)",
          "&:hover": { boxShadow: "0 6px 20px rgba(0,109,119,0.42)" },
        },
      },
    },
    MuiTab:       { styleOverrides: { root: { textTransform: "none", fontWeight: 600, minHeight: 44 } } },
    MuiChip:      { styleOverrides: { root: { fontWeight: 600 } } },
    MuiTextField: { defaultProps: { size: "small" } },
    MuiStepIcon: {
      styleOverrides: {
        root: { "&.Mui-active": { color: "#006D77" }, "&.Mui-completed": { color: "#16a34a" } },
      },
    },
  },
});
const styles={
    promoInput: { display: "flex", gap: "8px", marginTop: "12px" },
    input: { flex: 1, border: "1px solid #D1D5DB", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", fontFamily: "inherit" },
    button: { padding: "10px 20px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "14px", fontWeight: "600" },
    primaryButton: { backgroundColor: "#006D77", color: "#fff" },
    secondaryButton: { backgroundColor: "#fff", color: "#006D77", border: "1px solid #006D77" },
    dangerButton: { backgroundColor: "#EF4444", color: "#fff" },
    promoTag: { display: "inline-flex", alignItems: "center", gap: "8px", backgroundColor: "#D1FAE5", color: "#065F46", padding: "8px 12px", borderRadius: "8px", fontSize: "14px", fontWeight: "600", marginTop: "8px" },
    promoCard: { border: "1px solid #E5E7EB", borderRadius: "12px", padding: "16px", marginBottom: "12px", cursor: "pointer" },
    inputGroup: { marginBottom: "20px" },
}
const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00",
  "12:00", "13:00", "14:00", "15:00",
  "16:00", "17:00", "18:00",
];

// const TODAY = new Date().toISOString().split("T")[0];
const STEPS = ["Your Details", "Schedule", "Review & Confirm"];

function emptyForm() {
  return { patient_name: "", patient_phone: "", patient_address: "", start_date: "",end_date: "", scheduled_time: "", notes: "",promo_code: {} };
}

function StatPill({ icon, label }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 0.7,
      bgcolor: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.22)",
      borderRadius: 20, px: 1.5, py: 0.5,
    }}>
      <Box sx={{ "& svg": { fontSize: 13, display: "block" }, color: "rgba(255,255,255,0.9)" }}>{icon}</Box>
      <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600, color: "rgba(255,255,255,0.95)", whiteSpace: "nowrap" }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function NursingPhysiotherapyPage() {
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [tab,          setTab]          = useState(0);
  const [services,     setServices]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [bookingDlg,   setBookingDlg]   = useState({ open: false, service: null });
  const [step,         setStep]         = useState(0);
  const [success,      setSuccess]      = useState(false);
  const [form,         setForm]         = useState(emptyForm());
  const [snack,        setSnack]        = useState({ open: false, msg: "", sev: "success" });
  const servicesFetchedRef = useRef(false);



  // promo
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showAvailablePromos, setShowAvailablePromos] = useState(false);
  const [availablePromos, setAvailablePromos] = useState([]);
  const [promoMsg, setPromoMsg] = useState("")
  const [promotype, setPromotype] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [acceptedTerms,setAcceptedTerms]=useState(false)


  const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });

   const justAppliedPromo = React.useRef(false);

  // ─── Available promos fetch ────────────────────────────────────────────────
  useEffect(() => {
    const fetchAvailablePromos = async () => {
      try {
        const userId = sessionStorage.getItem("userId");
        const response = await axios.get(
          `${process.env.REACT_APP_APPLY_PROMOTION_API}/available?service_type=nursingAndPhysio&user_id=${userId}`,
          { headers: { "Content-Type": "application/json" } }
        );
        const data = response.data;
        if (data?.success) setAvailablePromos(data.promotions || []);
      } catch (error) {
        console.error("Error fetching promos:", error);
      }
    };
    fetchAvailablePromos();
  }, []);

   // ─── Promo helpers ─────────────────────────────────────────────────────────
    const applyPromoCode = async (code) => {
      const userId = sessionStorage.getItem("userId");
      const payload = {
        promo_code: code.toUpperCase(),
        service_type:"nursingAndPhysio",
        user_id: userId,
        order_price: bookingDlg.service?.price,
      };
      setPromoLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_APPLY_PROMOTION_API}/apply`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );
        const data = response.data;
        if (data.success) {
          justAppliedPromo.current = true;
          setAppliedPromo({ code: data.promo_code, discount: data.discount_amount, title: data.title ,discount_perc: data.discount_perc});
          setPromoCode(""); setShowAvailablePromos(false);
          setForm({
            ...form,
            promo_code: { code: data.promo_code, discount: data.discount_amount, title: data.title,discount_perc: data.discount_perc }
          });
          setPromoMsg("Applied")
          setPromotype("success")
          setDialogMessage(data.message || "Promo code applied successfully!"); setDialogOpen(true);
        } else {
          setPromoMsg("Invalid")
          setPromotype("error")
          setDialogMessage(data.error || "Invalid promo code"); setDialogOpen(true);
        }
      } catch {
        setPromoMsg("Invalid")
        setDialogMessage("Failed to apply promo code. Please try again."); setDialogOpen(true);
      } finally {
        setPromoLoading(false);
      }
    };
    const removePromoCode = () => {
    setAppliedPromo(null); setDialogMessage("Promo code removed"); setDialogOpen(true);setPromoMsg("Removed!")
  };

  // ── Fetch services ───────────────────────────────────────────────────────────
  const fetchServices = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/nursingphysiotherapy/services`, { headers: getAuthHeader() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.success) setServices(d.services || []);
      else { setError(d.error || "Failed to load services"); notify(d.error || "Failed to load", "error"); }
    } catch (err) {
      console.error("fetchServices:", err);
      setError("Cannot reach server. Please try again.");
      notify("Cannot reach server", "error");
    }
    setLoading(false);
  }, []);

  // useEffect(() => { fetchServices(); }, [fetchServices]);
  useEffect(() => {
    if (servicesFetchedRef.current) return;
    servicesFetchedRef.current = true;
    fetchServices();
  }, []);

  // ── Booking flow ─────────────────────────────────────────────────────────────
  const openBooking  = (svc) => { setForm(emptyForm()); setStep(0); setSuccess(false); setBookingDlg({ open: true, service: svc }); };
  const closeBooking = ()    => { setBookingDlg({ open: false, service: null }); setSuccess(false);setAppliedPromo(null);setPromoCode("");  };

  const validateStep = () => {
    if (step === 0) {
      if (!form.patient_name.trim())       { notify("Enter patient name", "warning");          return false; }
      if (!form.patient_phone.trim())      { notify("Enter phone number", "warning");          return false; }
      if (form.patient_phone.length < 10) { notify("Enter valid 10-digit number", "warning"); return false; }
      if (!form.patient_address.trim())    { notify("Enter home address", "warning");          return false; }
    }
    if (step === 1) {
      if (!form.start_date) { notify("Select a start date", "warning");      return false; }
      if (!form.end_date) { notify("Select a end date", "warning");      return false; }
      if (!form.scheduled_time) { notify("Select a time slot", "warning"); return false; }
    }
    return true;
  };

  const submitBooking = async () => {
    if (!bookingDlg.service) return;
    const userId = getUserId();
    try {
      const r = await fetch(`${API}/nursingphysiotherapy/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({ ...form, service_id: bookingDlg.service._id, user_id: userId || "" }),
      });
      const d = await r.json();
      if (d.success) {
        setSuccess(true);
      }
    
      else notify(d.error || "Booking failed", "error");
    } catch { notify("Booking failed. Try again.", "error"); }
  };

  // ── Derived ──────────────────────────────────────────────────────────────────
  const CATS     = ["all", "nursing", "physiotherapy"];
  const filtered = services.filter((s) => tab === 0 || s.category === CATS[tab]);
  const nurCnt   = services.filter((s) => s.category === "nursing").length;
  const phyCnt   = services.filter((s) => s.category === "physiotherapy").length;

  return (
    <ThemeProvider theme={ayujaTheme}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* ── Outer wrapper — matches Header.jsx sidebar/AppBar offsets ── */}
      <Box sx={{
        bgcolor: "background.default", minHeight: "100vh",
        ml: { xs: 0, md: 0},
        pt: { xs:0, md: 0 },
        width: { xs: "100%", md: "100%" },
        boxSizing: "border-box", overflowX: "hidden", pb: 6,
      }}>
            
        {/* ── Sticky page header ── */}
        <Box sx={{
          bgcolor: "white", borderBottom: "1px solid rgba(0,109,119,0.12)",
          px: { xs: 2, sm: 3, md: 4 },
          position: "sticky", top: { xs: 0, md: 0 },
          zIndex: 99, boxShadow: "0 1px 8px rgba(0,109,119,0.06)",
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Box>
            <IconButton onClick={() => navigate(-1)} sx={{ color: "#006D77", p: { xs: 0.5, sm: 1 } }}>
              </IconButton>
              <Typography variant="h6" color="primary.main" fontWeight={800} sx={{ fontSize: { xs: 15, sm: 19, md: 21 } }}>
                Nursing & Physiotherapy
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 11, sm: 12 } }}>
                Professional home healthcare — book a certified specialist today
              </Typography>
            </Box>
          </Box>

          <Tabs
            value={tab} onChange={(_, v) => setTab(v)}
            variant={isMobile ? "fullWidth" : "standard"} scrollButtons={false}
            sx={{
              mt: 1,
              "& .MuiTabs-indicator": { backgroundColor: "primary.main", height: 3, borderRadius: 2 },
              "& .Mui-selected": { color: "primary.main !important" },
            }}
          >
            <Tab label="All Services"  sx={{ fontSize: { xs: 12, sm: 13 }, px: { xs: 1, sm: 2 } }} />
            <Tab icon={<LocalHospitalIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />} iconPosition="start"
              label="Nursing" sx={{ fontSize: { xs: 12, sm: 13 }, px: { xs: 1, sm: 2 } }} />
            <Tab icon={<FitnessCenterIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />} iconPosition="start"
              label="Physiotherapy" sx={{ fontSize: { xs: 12, sm: 13 }, px: { xs: 1, sm: 2 } }} />
          </Tabs>
        </Box>

        {/* ── Hero banner ── */}
        <Box sx={{
          backgroundImage: `
                    linear-gradient(135deg, rgba(0,109,119,0.9) 0%, rgba(0, 169, 181, 0.68) 100%),
                    url(${bg})
                  `,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat" ,
          px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2.5, sm: 3.5 },
          position: "relative", overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", right: -40, top: -40, width: 200, height: 200, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", right: 60, bottom: -60, width: 140, height: 140, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
          <Typography sx={{ color: "white", fontWeight: 800, fontSize: { xs: 16, sm: 20, md: 24 }, mb: 0.5, position: "relative" }}>
            Healthcare at Your Doorstep 🏠
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: { xs: 12, sm: 13 }, mb: 2, position: "relative" }}>
            Certified professionals · Scheduled visits · Transparent pricing
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ position: "relative" }}>
            <StatPill icon={<LocalHospitalIcon />} label={`${nurCnt} Nursing`} />
            <StatPill icon={<FitnessCenterIcon />} label={`${phyCnt} Physio`} />
            <StatPill icon={<VerifiedUserIcon />}  label="Certified Staff" />
            <StatPill icon={<StarIcon />}           label="Top Rated" />
          </Stack>
        </Box>

        {/* ── Content ── */}
        <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2.5, md: 3 }, pt: { xs: 3, sm: 4 } }}>

          {loading && (
            <Box sx={{ textAlign: "center", py: 12 }}>
              <CircularProgress size={48} sx={{ color: "primary.main" }} />
              <Typography mt={2} color="text.secondary" fontSize={14} fontWeight={500}>Loading services…</Typography>
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Alert severity="error" sx={{ maxWidth: 480, mx: "auto", borderRadius: 2, mb: 2 }}>{error}</Alert>
              <Button variant="outlined" color="primary" onClick={fetchServices}>Retry</Button>
            </Box>
          )}

          {!loading && !error && filtered.length === 0 && (
            <Box sx={{ textAlign: "center", py: 12 }}>
              <MedicalServicesIcon sx={{ fontSize: 64, color: "primary.light", opacity: 0.3, mb: 2 }} />
              <Typography fontWeight={700} color="text.secondary" mb={0.5}>No services available</Typography>
              <Typography fontSize={13} color="text.secondary">
                {tab === 0 ? "No services added yet." : `No ${CATS[tab]} services currently available.`}
              </Typography>
            </Box>
          )}

          {/* ── CSS Grid for perfectly uniform card heights & widths ── */}
          {!loading && !error && filtered.length > 0 && (
            <Box sx={{
              display: "grid",
              gap: { xs: 2, sm: 2.5, md: 3 },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              alignItems: "stretch",
            }}>
              {filtered.map((svc) => {
                const isNursing = svc.category === "nursing";
                const accentClr = isNursing ? "#006D77" : "#b8654a";
                const accentBg  = isNursing ? alpha("#006D77", 0.07) : alpha("#E29578", 0.09);
                const badgeBg   = isNursing ? alpha("#006D77", 0.1)  : alpha("#E29578", 0.14);

                return (
                  <Card key={svc._id} sx={{
                    display: "flex", flexDirection: "column",
                    borderRadius: 3, overflow: "hidden",
                    border: `1px solid ${alpha(accentClr, 0.15)}`,
                  }}>
                    {/* Top accent stripe */}
                    <Box sx={{
                      height: 4,
                      background: isNursing
                        ? "linear-gradient(90deg, #006D77, #00949f)"
                        : "linear-gradient(90deg, #E29578, #c97a60)",
                    }} />

                    <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column" }}>
                      {/* Header row */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Avatar sx={{
                          bgcolor: accentBg, color: accentClr,
                          width: { xs: 44, sm: 48 }, height: { xs: 44, sm: 48 },
                          boxShadow: `0 4px 12px ${alpha(accentClr, 0.18)}`,
                        }}>
                          {isNursing ? <LocalHospitalIcon sx={{ fontSize: 22 }} /> : <FitnessCenterIcon sx={{ fontSize: 22 }} />}
                        </Avatar>
                        <Chip
                          label={isNursing ? "Nursing" : "Physiotherapy"}
                          size="small"
                          sx={{ bgcolor: badgeBg, color: accentClr, fontWeight: 700, fontSize: 10, border: `1px solid ${alpha(accentClr, 0.2)}` }}
                        />
                      </Box>

                      <Typography variant="h6" sx={{ fontSize: { xs: 14, sm: 15 }, lineHeight: 1.35, mb: 1, fontWeight: 700 }}>
                        {svc.name}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{
                        mb: 1.5, lineHeight: 1.65, fontSize: { xs: 12, sm: 13 },
                        flexGrow: 1,
                        display: "-webkit-box", WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {svc.description || "Professional home healthcare service by certified specialists."}
                      </Typography>

                      {/* Condition tags */}
                      {svc.conditions_covered?.length > 0 && (
                        <Box sx={{ mb: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {svc.conditions_covered.slice(0, 3).map((c, i) => (
                            <Chip key={i} label={c} size="small" variant="outlined"
                              sx={{ fontSize: "0.62rem", borderColor: alpha(accentClr, 0.3), color: accentClr }} />
                          ))}
                          {svc.conditions_covered.length > 3 && (
                            <Chip label={`+${svc.conditions_covered.length - 3}`} size="small" sx={{ fontSize: "0.62rem" }} />
                          )}
                        </Box>
                      )}

                      <Divider sx={{ my: 1.5, borderColor: alpha(accentClr, 0.1) }} />

                      {/* Price & duration */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
                            <CurrencyRupeeIcon sx={{ fontSize: 15, color: "primary.main" }} />
                            <Typography fontWeight={800} color="primary.main" sx={{ fontSize: { xs: 16, sm: 18 } }}>
                              {svc.price}
                            </Typography>
                          </Box>
                          {svc.price_b2b && (
                            <Typography variant="caption" color="text.secondary" fontSize={10}>
                              B2B: ₹{svc.price_b2b}
                            </Typography>
                          )}
                        </Box>
                        {svc.duration && (
                          <Box sx={{
                            display: "flex", alignItems: "center", gap: 0.5,
                            bgcolor: alpha(accentClr, 0.07), px: 1, py: 0.4, borderRadius: 10,
                          }}>
                            <AccessTimeIcon sx={{ fontSize: 12, color: accentClr }} />
                            <Typography variant="caption" fontWeight={600} sx={{ color: accentClr, fontSize: 11 }}>
                              {svc.duration}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                              

                    {/* Book button — pinned to bottom */}
                    <CardActions sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 }, pt: 0 }}>
                      <Button
                        fullWidth variant="contained"
                        startIcon={<BookmarkAddIcon />}
                        onClick={() => openBooking(svc)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          py: 1, fontSize: { xs: 12, sm: 13 },
                          ...(isNursing
                            ? {}
                            : {
                                background: "linear-gradient(135deg, #E29578 0%, #c97a60 100%)",
                                color: "white",
                                boxShadow: "0 4px 14px rgba(226,149,120,0.32)",
                                "&:hover": { boxShadow: "0 6px 20px rgba(226,149,120,0.44)" },
                              }),
                        }}
                      >
                        Book Now
                      </Button>
                    </CardActions>
                  </Card>
                );
              })}
            </Box>
          )}
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════════
          BOOKING DIALOG
      ══════════════════════════════════════════════════════════════ */}
      <Dialog open={bookingDlg.open} onClose={closeBooking} maxWidth="sm" fullWidth
        fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, overflow: "hidden" } }}>
        <Box sx={{ height: 5, background: "linear-gradient(90deg, #006D77, #00949f)" }} />
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e8f4f5", pb: 1.5, pt: 2 }}>
          <Box>
            <Typography fontWeight={800} fontSize={{ xs: 15, sm: 18 }} color="primary.main">Book Service</Typography>
            {bookingDlg.service && (
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {bookingDlg.service.name} · ₹{bookingDlg.service.price}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={closeBooking}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
          {!success ? (
            <>
              <Stepper activeStep={step} alternativeLabel={isMobile}
                sx={{ mb: 3.5, "& .MuiStepLabel-label": { fontSize: { xs: 10, sm: 12 }, fontWeight: 600 } }}>
                {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
              </Stepper>

              {/* Step 0 */}
              {step === 0 && (
                <Stack spacing={2.5}>
                  <TextField label="Patient Full Name *" fullWidth value={form.patient_name}
                    onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 17, color: "primary.main" }} /></InputAdornment> }} />
                  <TextField label="Patient Age (Optional)" fullWidth value={form.age} type="number"
                    onChange={(e) => setForm({ ...form, age: e.target.value })}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 17, color: "primary.main" }} /></InputAdornment> }} />
                  <TextField label="Phone Number *" fullWidth value={form.patient_phone}
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) => setForm({ ...form, patient_phone: e.target.value.replace(/\D/g, "") })}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 17, color: "primary.main" }} /></InputAdornment> }}
                    helperText="10-digit mobile number" />
                  <TextField label="Home Address *" fullWidth multiline rows={3} value={form.patient_address}
                    onChange={(e) => setForm({ ...form, patient_address: e.target.value })}
                    placeholder="House no., street, area, city, pincode…"
                    InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ fontSize: 17, color: "primary.main" }} /></InputAdornment> }} />
                </Stack>
              )}

              {/* Step 1 */}
              {step === 1 && (
                <Stack spacing={2.5}>
                  <Grid container spacing={2}>
  <Grid size={{ xs: 12, md: 6 }}>
    <TextField
      label="Start Date *"
      type="date"
      fullWidth
      value={form.start_date}
      onChange={(e) =>
        setForm({ ...form, start_date: e.target.value })
      }
      InputLabelProps={{ shrink: true }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CalendarTodayIcon
              sx={{ fontSize: 17, color: "primary.main" }}
            />
          </InputAdornment>
        ),
      }}
    />
  </Grid>

  <Grid size={{ xs: 12, md: 6 }}>
    <TextField
      label="End Date *"
      type="date"
      fullWidth
      value={form.end_date}
      onChange={(e) =>
        setForm({ ...form, end_date: e.target.value })
      }
      InputLabelProps={{ shrink: true }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CalendarTodayIcon
              sx={{ fontSize: 17, color: "primary.main" }}
            />
          </InputAdornment>
        ),
      }}
    />
  </Grid>
</Grid>
                  <Box>
                    <Typography variant="body2" fontWeight={700} mb={1.5} color="text.secondary">Select Time Slot *</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {TIME_SLOTS.map((t) => (
                        <Chip key={t} label={t} clickable
                          color={form.scheduled_time === t ? "primary" : "default"}
                          variant={form.scheduled_time === t ? "filled" : "outlined"}
                          onClick={() => setForm({ ...form, scheduled_time: t })}
                          size={isMobile ? "small" : "medium"}
                          icon={<ScheduleIcon sx={{ fontSize: "13px !important" }} />}
                          sx={{ borderRadius: 2, fontWeight: 600, ...(form.scheduled_time !== t && { borderColor: alpha("#006D77", 0.3) }) }} />
                      ))}
                    </Box>
                  </Box>
                  <TextField label="Notes (optional)" fullWidth multiline rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Medical history, conditions, special requirements…" />
                </Stack>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <Box>
                     <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#FEF3C7",
                      border: "1px solid #FCD34D"
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#92400E"
                      }}
                    >  🎁 Have a promo code? Apply it below to get discounts on your booking when you pay later. You can also view all available offers.
                    </Typography>
                  </Box>
                              {appliedPromo && (
                                <div style={styles.summaryRow}>
                                  <span style={{ ...styles.summaryLabel, color: "#d97706", fontWeight: "600" }}>🏷️ Promo ({appliedPromo.code})</span>
                                  {appliedPromo.discount_perc == 0 &&(<span style={{ ...styles.summaryValue, color: "#d97706" }}> -₹{appliedPromo.discount.toFixed(2)}</span>)}
                                  {appliedPromo.discount_perc > 0 &&(<span style={{ ...styles.summaryValue, color: "#d97706" }}>-{appliedPromo.discount_perc}%</span>)}
                                </div>
                              )}
                    
                              <div style={styles.divider} />
                    
                              
                              {/* Promo section */}
                              {!appliedPromo ? (
                                <>
                                  <div style={styles.promoInput}>
                                    <input type="text" placeholder="Enter promo code" value={promoCode}
                                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())} style={styles.input} />
                                    <button onClick={() => applyPromoCode(promoCode)} disabled={!promoCode || promoLoading}
                                      style={{ ...styles.button, ...styles.primaryButton, opacity: !promoCode ? 0.5 : 1 }}>
                                      {promoLoading ? "Applying..." : "Apply"}
                                    </button>
                                  </div>
                                  <button onClick={() => setShowAvailablePromos(!showAvailablePromos)}
                                    style={{ ...styles.button, ...styles.secondaryButton, width: "100%", marginTop: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                                    <Tag size={16} /> View Available Offers
                                    {showAvailablePromos ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                  </button>
                                  {showAvailablePromos && availablePromos.length > 0 && (
                                    <div style={{ marginTop: "12px" }}>
                                      {availablePromos.map((promo, index) => (
                                        <div key={index} style={styles.promoCard}
                                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#006D77"; e.currentTarget.style.backgroundColor = "#F0FDFA"; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.backgroundColor = "#fff"; }}
                                          onClick={() => applyPromoCode(promo.promo_code)}>
                                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                                            <div>
                                              <div style={{ fontSize: "16px", fontWeight: "700", color: "#111827", marginBottom: "4px" }}>{promo.promo_code}</div>
                                              <div style={{ fontSize: "14px", color: "#6B7280", marginBottom: "8px" }}>{promo.description}</div>
                                              <div style={{ fontSize: "12px", color: "#9CA3AF" }}>
                                                Min order: ₹{promo.min_order_value}{promo.max_discount > 0 && ` • Max discount: ₹${promo.max_discount}`}
                                              </div>
                                            </div>
                                            <button style={{ ...styles.button, ...styles.primaryButton, fontSize: "12px", padding: "6px 12px" }}>Apply</button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <div style={styles.promoTag}><Tag size={16} />{appliedPromo.code} Applied</div>
                                  <button onClick={removePromoCode} style={{ ...styles.button, ...styles.dangerButton, fontSize: "12px", padding: "6px 12px" }}>Remove</button>
                                </div>
                              )}
                         
                  <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>Review your details carefully before confirming.</Alert>
                  <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden", border: "1px solid rgba(0,109,119,0.15)" }}>
                    <Box sx={{ background: "linear-gradient(135deg, #006D77, #00949f)", px: 2.5, py: 1.8 }}>
                      <Typography fontWeight={800} color="white" fontSize={{ xs: 13, sm: 15 }}>{bookingDlg.service?.name}</Typography>
                      <Typography variant="caption" sx={{ color: alpha("#fff", 0.8) }}>
                        {bookingDlg.service?.category} · ₹{bookingDlg.service?.price}
                      </Typography>
                    </Box>
                    {[
                      [<PersonIcon />,         "Patient",   form.patient_name],
                      [<PhoneIcon />,           "Phone",     form.patient_phone],
                      [<PersonIcon />,          "Age",        form.age],
                      [<LocationOnIcon />,      "Address",   form.patient_address],
                      [<EventIcon />,           "Start Date",      form.start_date],
                      [<EventIcon />,           "End Date",      form.end_date],
                      [<ScheduleIcon />,        "Time",      form.scheduled_time],
                      [<MedicalServicesIcon />, "Notes",     form.notes || "—"],
                    ].map(([icon, label, value], i) => (
                      <Box key={i} sx={{
                        display: "flex", alignItems: "flex-start", gap: 1.5,
                        px: 2.5, py: 1.2,
                        bgcolor: i % 2 === 0 ? "white" : alpha("#006D77", 0.025),
                        borderBottom: i < 5 ? "1px solid #f0f7f8" : "none",
                      }}>
                        <Box sx={{ color: "primary.main", mt: 0.3, "& svg": { fontSize: 15 } }}>{icon}</Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}
                            textTransform="uppercase" letterSpacing={0.6} display="block" fontSize={9.5}>{label}</Typography>
                          <Typography variant="body2" fontWeight={600} fontSize={{ xs: 12, sm: 13 }}>{value}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                   <FormControlLabel style={{paddingBottom:16,display: "flex", justifyContent: "center", alignItems: "center",}}
                                                  
                    control={
                      <Checkbox
                        size="small"
                        checked={acceptedTerms || false}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Accept Ayuja's{" "}
                        <a href="/terms&conditions" target="_blank" rel="noopener noreferrer"
                          style={{ color: "#007a85", fontWeight: "bold", textDecoration: "none" }}>
                          Terms and Conditions
                        </a>
                          <span style={{ color: "red", marginLeft: 2 ,fontSize:19}}>*</span>
                      </Typography>
                    }
                  />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: "center", py: { xs: 3, sm: 5 } }}>
              <Box sx={{
                width: 90, height: 90, borderRadius: "50%",
                bgcolor: alpha("#16a34a", 0.1),
                display: "flex", alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 2.5,
              }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 52, color: "success.main" }} />
              </Box>
              <Typography variant="h5" fontWeight={800} gutterBottom fontSize={{ xs: 18, sm: 22 }} color="primary.main">
                Booking Confirmed! 🎉
              </Typography>
              <Typography color="text.secondary" mb={2.5} fontSize={{ xs: 13, sm: 14 }} lineHeight={1.6}>
                Your booking for <strong>{bookingDlg.service?.name}</strong> has been received.
                Our team will contact you shortly to confirm the schedule.
              </Typography>
              <Alert severity="success" icon={<TagIcon />} sx={{ textAlign: "left", borderRadius: 2 }}>
                Track your booking via <strong>"Order - Nursing & Physiotherapy"</strong> — linked to your account automatically.
              </Alert>
            </Box>
          )}
        </DialogContent>

        {!success ? (
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1, borderTop: "1px solid #e8f4f5" }}>
            {step > 0 && <Button onClick={() => setStep(step - 1)} color="inherit" startIcon={<ArrowBackIcon />}>Back</Button>}
            <Box sx={{ flex: 1 }} />
            {step < 2
              ? <Button variant="contained" onClick={() => { if (validateStep()) setStep(step + 1); }} sx={{ px: 3 }}>Next →</Button>
              : <Button variant="contained" color="success" startIcon={<CheckCircleOutlineIcon />} onClick={submitBooking} disabled={!acceptedTerms} sx={{ px: 3 }}>Confirm Booking</Button>}
          </DialogActions>
        ) : (
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: "1px solid #e8f4f5" }}>
            <Button variant="contained" fullWidth onClick={closeBooking} size="large">Done</Button>
          </DialogActions>
        )}
      </Dialog>


      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}